// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Drawer, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { normalizeErrorString } from '@medplum/core';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

interface TreatmentPatientDrawerProps {
  patientId: string | undefined;
  patientName: string;
  opened: boolean;
  onClose: () => void;
}

export function TreatmentPatientDrawer({
  patientId,
  patientName,
  opened,
  onClose,
}: TreatmentPatientDrawerProps): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<Patient>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!opened || !patientId) {
      return;
    }
    setLoading(true);
    setError(undefined);
    medplum
      .readResource('Patient', patientId)
      .then(setPatient)
      .catch((loadError) => setError(normalizeErrorString(loadError)))
      .finally(() => setLoading(false));
  }, [medplum, opened, patientId]);

  const phone = patient?.telecom?.find((item) => item.system === 'phone')?.value ?? 'Unavailable';
  const email = patient?.telecom?.find((item) => item.system === 'email')?.value ?? 'Unavailable';

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="min(520px, 100vw)"
      title={<Title order={2}>{patient?.name?.[0]?.text ?? patientName}</Title>}
    >
      {loading && <Loader size="sm" />}
      {error && <Text c="red">{error}</Text>}
      {!loading && !error && (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Treatment maintenance patient profile
          </Text>
          <Stack gap="xs">
            <Text size="sm">
              <b>Phone:</b> {phone}
            </Text>
            <Text size="sm">
              <b>Email:</b> {email}
            </Text>
            <Text size="sm">
              <b>Birth date:</b> {patient?.birthDate ?? 'Unavailable'}
            </Text>
          </Stack>
          <Group>
            <Button color="polaris" onClick={() => navigate(`/Patient/${patientId}`)}>
              View chart
            </Button>
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}
